import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "moment";
import _ from "lodash";
import { useI18n } from "Hooks";

// ds
import {
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
  DSProvider,
} from "@toddle-edu/ds-web";
import { PlayFilled, PauseFilled } from "@toddle-edu/ds-icons";

// components
import WaveLoader from "./components/WaveLoader";

// utils
import { isTouchableV3 } from "Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";

// scss
import classes from "./StreamingAudioPlayer.scss";

const AUDIO_SPEED_OPTIONS = [
  {
    key: "dropdown_title",
    label: "Playback speed",
    isItemGroup: true,
    options: [
      { key: "0.25", label: "0.25x" },
      { key: "0.5", label: "0.5x" },
      { key: "0.75", label: "0.75x" },
      { key: "1", label: "1x" },
      { key: "1.5", label: "1.5x" },
      { key: "2", label: "2x" },
    ],
  },
];

const WAVEFORM_BAR_COUNT = 80;

/**
 * Generates a hash code from a string
 * @param {string} str - The string to hash
 * @returns {number} - A numeric hash value
 */
const hashStringToNumber = str => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Generates realistic speech-like waveform bars based on a unique id
 * @param {string} id - Unique identifier to seed the random generation
 * @param {number} count - Number of bars to generate
 * @returns {Array<number>} - Array of bar heights (0.15 to 1)
 */
const generateWaveformBars = (id, count = WAVEFORM_BAR_COUNT) => {
  const bars = [];
  const idHash = hashStringToNumber(id);

  // Seeded random function based on id hash
  const seededRandom = seed => {
    const x = Math.sin(idHash * 0.001 + seed * 9999.7) * 10000;
    return x - Math.floor(x);
  };

  // Generate word boundaries (speech has natural gaps between words)
  const wordCount = 4 + Math.floor(seededRandom(100) * 6); // 4-9 words
  const wordBoundaries = [];
  for (let w = 0; w < wordCount; w++) {
    const wordStart = Math.floor((w / wordCount) * count);
    const wordEnd = Math.floor(
      ((w + 0.7 + seededRandom(w * 50) * 0.25) / wordCount) * count
    );
    wordBoundaries.push({ start: wordStart, end: wordEnd });
  }

  // Check if index is within a word
  const isInWord = index => {
    return _.some(
      wordBoundaries,
      ({ start, end }) => index >= start && index <= end
    );
  };

  // Get word intensity (words have varying loudness)
  const getWordIntensity = index => {
    for (let w = 0; w < wordBoundaries.length; w++) {
      const { start, end } = wordBoundaries[w];
      if (index >= start && index <= end) {
        const wordLength = end - start;
        const posInWord = (index - start) / Math.max(wordLength, 1);
        // Words typically start softer, peak in middle, end softer
        const wordEnvelope = Math.sin(posInWord * Math.PI);
        const wordBaseIntensity = 0.5 + seededRandom(w * 77) * 0.5;
        return wordEnvelope * wordBaseIntensity;
      }
    }
    return 0;
  };

  for (let i = 0; i < count; i++) {
    let height;

    if (isInWord(i)) {
      // Within a word - generate speech-like amplitude
      const wordIntensity = getWordIntensity(i);

      // High frequency variation (vocal formants)
      const formant1 = Math.sin(i * 0.8 + seededRandom(i * 3) * Math.PI) * 0.15;
      const formant2 = Math.sin(i * 1.3 + seededRandom(i * 5) * Math.PI) * 0.1;

      // Random micro-variations
      const microNoise = (seededRandom(i * 13.7) - 0.5) * 0.25;

      // Occasional emphasis peaks (stressed syllables)
      const stressPeak = seededRandom(i * 7.1) > 0.85 ? 0.2 : 0;

      height =
        0.3 +
        wordIntensity * 0.6 +
        formant1 +
        formant2 +
        microNoise +
        stressPeak;
    } else {
      // Between words - minimal ambient noise
      height = 0.1 + seededRandom(i * 11.3) * 0.12;
    }

    // Clamp and add slight randomness for organic feel
    height = height + (seededRandom(i * 17.9) - 0.5) * 0.08;
    height = Math.min(1, Math.max(0.08, height));

    // First bar should be minimum height
    if (i === 0) {
      height = 0.08;
    }

    bars.push(height);
  }

  return bars;
};

const StreamingAudioPlayer = props => {
  const {
    id,
    audioElement,
    defaultPlaybackSpeed = "1",
    onAudioSpeedChange,
    containerStyle = {},
    playerButtonIconStyle = {},
    playerIconClass,
    playbackSpeedTriggerClass,
    activePlaybackSpeedTriggerClass,
    playbackSpeedButtonProps = {},
    buttonElement = null,
    testId = "streamingAudioPlayer",
    isAppearanceLikeMediaCard = false,
    waveColor = "#CCCCCC",
    progressColor = "#666666",
    cursorColor = "#000000",
  } = props;

  const [playing, setPlaying] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [audioSpeed, setAudioSpeed] = useState(defaultPlaybackSpeed);
  const [currentTime, setCurrentTime] = useState("00:00");
  const [visibleBarsCount, setVisibleBarsCount] = useState(0);
  const [isSpeedDropdownOpen, setIsSpeedDropdownOpen] = useState(false);

  const waveformRef = useRef(null);

  const { t } = useI18n();

  // Generate unique waveform based on id
  const waveformBars = useMemo(
    () => generateWaveformBars(id, WAVEFORM_BAR_COUNT),
    [id]
  );

  const MAX_SCREEN_WIDTH = 1200;
  const isTouchableDevice = isTouchableV3(MAX_SCREEN_WIDTH);

  // Calculate how many bars can fit in the container
  useEffect(() => {
    if (!waveformRef.current) return;

    const calculateVisibleBars = () => {
      const containerWidth = waveformRef?.current?.offsetWidth || 0;
      const barWidth = 2; // px
      const gapWidth = 2; // px
      const barsCount = Math.floor(
        (containerWidth + gapWidth) / (barWidth + gapWidth)
      );

      setVisibleBarsCount(Math.min(barsCount, waveformBars.length));
    };

    const resizeObserver = new ResizeObserver(calculateVisibleBars);
    resizeObserver.observe(waveformRef.current);

    return () => resizeObserver.disconnect();
  }, [waveformBars.length]);

  useEffect(() => {
    if (!audioElement) return;

    audioElement.playbackRate = parseFloat(audioSpeed);

    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    const handleEnded = () => {
      setPlaying(false);
      setHasEnded(true);
      setProgress(0);
      setCurrentTime("00:00");
      audioElement.currentTime = 0;
    };

    const handleTimeUpdate = () => {
      const current = audioElement.currentTime;
      const duration = audioElement.duration;

      if (duration > 0) {
        setProgress((current / duration) * 100);
      }

      const formattedTime = moment
        .duration(~~current, "seconds")
        .format("mm:ss", { trim: false });
      setCurrentTime(formattedTime);
    };

    audioElement.addEventListener("play", handlePlay);
    audioElement.addEventListener("pause", handlePause);
    audioElement.addEventListener("ended", handleEnded);
    audioElement.addEventListener("timeupdate", handleTimeUpdate);

    // Sync initial state
    setPlaying(!audioElement.paused);

    return () => {
      audioElement.removeEventListener("play", handlePlay);
      audioElement.removeEventListener("pause", handlePause);
      audioElement.removeEventListener("ended", handleEnded);
      audioElement.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioElement, audioSpeed]);

  const handlePlayPause = useCallback(() => {
    if (!audioElement || !hasEnded) return;

    if (audioElement.paused) {
      audioElement.play();
    } else {
      audioElement.pause();
    }
  }, [audioElement, hasEnded]);

  const handleWaveformClick = useCallback(
    e => {
      if (!audioElement || !hasEnded) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * audioElement.duration;

      audioElement.currentTime = newTime;
    },
    [audioElement, hasEnded]
  );

  const changeAudioSpeed = useCallback(
    e => {
      const speed = e.key;
      setAudioSpeed(speed);
      if (audioElement) {
        audioElement.playbackRate = parseFloat(speed);
      }
      onAudioSpeedChange?.(speed);
      setIsSpeedDropdownOpen(false);
    },
    [audioElement, onAudioSpeedChange]
  );

  const containerClasses = classNames({
    [classes.container]: true,
    [classes.mediaCardTypeContainer]: isAppearanceLikeMediaCard,
  });

  const playbackSpeedTriggerClasses = classNames({
    [classes.playbackSpeedTrigger]: true,
    [classes.playbackSpeedTriggerMediumSize]: isAppearanceLikeMediaCard,
    [playbackSpeedTriggerClass]: !_.isEmpty(playbackSpeedTriggerClass),
    [activePlaybackSpeedTriggerClass]:
      isSpeedDropdownOpen && !_.isEmpty(activePlaybackSpeedTriggerClass),
  });

  const getIcon = () => {
    if (!hasEnded) {
      return <WaveLoader size="sm" color={progressColor} />;
    }

    if (playing) {
      return <PauseFilled size={"xxxx-small"} style={playerButtonIconStyle} />;
    }

    return <PlayFilled size={"xxxx-small"} style={playerButtonIconStyle} />;
  };

  const getTooltipText = () => {
    if (!hasEnded) return "Loading";
    if (playing) return "Pause";
    return "Play";
  };

  return (
    <DSProvider dsVersion="2.0">
      <div className={containerClasses} style={containerStyle}>
        <div className={classes.buttonContainer}>
          <ConditionalWrapper
            condition={!isTouchableDevice}
            type="wrapperAsElement"
            wrapper={
              <Tooltip
                tooltip={getTooltipText()}
                placement="top"
                showArrow={true}
              />
            }
          >
            <IconButton
              icon={getIcon()}
              size={isAppearanceLikeMediaCard ? "medium" : "x-small"}
              variant={"neutral"}
              type={"plain"}
              onClick={handlePlayPause}
              testId={`${testId}-playPauseButton`}
              className={playerIconClass}
              disabled={!hasEnded}
            />
          </ConditionalWrapper>
        </div>

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          ref={waveformRef}
          className={classNames(classes.waveformContainer, {
            [classes.waveformDisabled]: !hasEnded,
          })}
          onClick={handleWaveformClick}
        >
          {!hasEnded ? (
            <span
              className={classNames("text-body", classes.speakingText)}
            >{`${t("ai:speaking")}...`}</span>
          ) : (
            <>
              {_.map(
                _.slice(waveformBars, 0, visibleBarsCount),
                (height, index) => {
                  const barProgress = (index / visibleBarsCount) * 100;
                  const isPlayed = barProgress <= progress;

                  return (
                    <div
                      key={index}
                      className={classes.waveformBar}
                      style={{
                        height: `${height * 100}%`,
                        backgroundColor: isPlayed ? progressColor : waveColor,
                      }}
                    />
                  );
                }
              )}
              <div
                className={classes.waveformCursor}
                style={{
                  left: `${progress}%`,
                  backgroundColor: cursorColor,
                }}
              />
            </>
          )}
        </div>

        <div className={classNames(classes.durationContainer, "text-label")}>
          {currentTime}
        </div>

        <div className={classes.rightContainer}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className={classes.audioSpeedControl}
            onClick={e => e.stopPropagation()}
          >
            <Dropdown
              overlay={
                <DropdownMenu
                  showSelection={true}
                  value={audioSpeed}
                  onClick={changeAudioSpeed}
                  options={AUDIO_SPEED_OPTIONS}
                  testId={`${testId}-audioSpeedDropdown`}
                />
              }
              visible={isSpeedDropdownOpen}
              onVisibleChange={setIsSpeedDropdownOpen}
            >
              <ConditionalWrapper
                condition={!isTouchableDevice}
                type="wrapperAsElement"
                wrapper={
                  <Tooltip
                    tooltip={t("ai:playback_speed")}
                    placement="top"
                    showArrow={true}
                  />
                }
              >
                <Button
                  className={playbackSpeedTriggerClasses}
                  variant="neutral"
                  type="outlined"
                  testId={`${testId}-playbackSpeed`}
                  size={isAppearanceLikeMediaCard ? "medium" : "x-small"}
                  {...playbackSpeedButtonProps}
                >
                  {`${audioSpeed}x`}
                </Button>
              </ConditionalWrapper>
            </Dropdown>
          </div>

          {buttonElement}
        </div>
      </div>
    </DSProvider>
  );
};

StreamingAudioPlayer.propTypes = {
  id: PropTypes.string.isRequired,
  audioElement: PropTypes.object.isRequired,
  defaultPlaybackSpeed: PropTypes.string,
  onAudioSpeedChange: PropTypes.func,
  containerStyle: PropTypes.object,
  playerButtonIconStyle: PropTypes.object,
  playerIconClass: PropTypes.string,
  playbackSpeedTriggerClass: PropTypes.string,
  playbackSpeedButtonProps: PropTypes.object,
  buttonElement: PropTypes.node,
  testId: PropTypes.string,
  isAppearanceLikeMediaCard: PropTypes.bool,
  waveColor: PropTypes.string,
  progressColor: PropTypes.string,
  cursorColor: PropTypes.string,
};

StreamingAudioPlayer.defaultProps = {
  defaultPlaybackSpeed: "1",
  containerStyle: {},
  playerButtonIconStyle: {},
  playbackSpeedButtonProps: {},
  testId: "streamingAudioPlayer",
  isAppearanceLikeMediaCard: false,
  waveColor: "#CCCCCC",
  progressColor: "#666666",
  cursorColor: "#000000",
};

export default StreamingAudioPlayer;
